import { OTP_RESEND_TIMEOUT, REGEX } from '@/constants';
import { PhoneNumberStatus, QueryKeys, VerificationOtpType } from '@/enums';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { IconEdit } from '@/icons/IconEdit';
import { ICountdownParams } from '@/interfaces';
import { ILoginModel, ISignupModel } from '@/interfaces/authentication';
import { IProfileModel } from '@/interfaces/profile';
import { updatePhoneNumber } from '@/services/authentication.service';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { signIn } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Countdown, { zeroPad } from 'react-countdown';
import { flushSync } from 'react-dom';
import { FormConfig, HookForm, Input } from './HookForm';

const CountDownRenderer = ({ minutes, seconds }: ICountdownParams) => (
  <span>
    {zeroPad(minutes)}:{zeroPad(seconds)}
  </span>
);

const AddOrChangePhoneComponent = ({ profileData, onComplete = () => {} }: { profileData?: IProfileModel; onComplete?: Function }) => {
  const [phoneNumberStatus, setPhoneNumberStatus] = useState(PhoneNumberStatus.unchecked);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [optCountdownStartTime, setOptCountdownStartTime] = useState<any>(null);
  const [formData, setFormData] = useState<any>();
  const [otpValue, setOtpValue] = useState<number>();
  const formRef = useRef<any | null>(null);
  const queryClient = useQueryClient();

  const formConfig: FormConfig = {
    initialValues: { phoneNumber: '' },
    validation: {
      phoneNumber: {
        required: 'Mobile number is required.',
        pattern: {
          value: REGEX.VALID_PHONE,
          message: 'Invalid phone number.',
        },
      },
      otp: {
        required: 'OTP is required!',
        minLength: {
          value: 4,
          message: 'Enter the valid 4 digits OTP',
        },
        maxLength: {
          value: 4,
          message: 'Enter the valid 4 digits OTP',
        },
        pattern: {
          value: REGEX.VALID_NUMBER,
          message: 'Invalid OTP',
        },
      },
    },
  };
  useEffect(() => {
    if (phoneNumberStatus === PhoneNumberStatus.temporarySignUpDone && 'OTPCredential' in window) {
      console.log(window.OTPCredential, 'otp');
      const input = document.querySelector('input[autocomplete="one-time-code"]') as HTMLInputElement;
      const phoneNumberInput = document.querySelector('input[name="phoneNumber"]') as HTMLInputElement;
      console.log(input, 'input');
      if (!input) return;
      const ac = new AbortController();
      const form = input?.closest('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          console.log('aborting');
          //  ac.abort();
        });
      }
      console.log(form, 'form');
      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: ac.signal,
        })
        .then((otp) => {
          console.log(otp, form, 'inside get');
          if (input && form && otp?.code) {
            flushSync(() => {
              setOtpValue(otp?.code);
            });
            console.log(otpValue);
            let payload = {
              phoneNumber: phoneNumberInput.value,
              otp: input.value,
            } as ILoginModel;
            console.log(payload);
            onSubmit(payload);
          }
        })
        .catch((err) => {
          console.log(err, form, 'sms read error');
        });
    }
  }, [phoneNumberStatus]);
  const triggerPhoneCheck = (data: ISignupModel | ILoginModel) => {
    setIsLoading(true);

    if (profileData?.Id) {
      updatePhoneNumber(
        profileData?.Id,
        data.phoneNumber.trim().replace(REGEX.REPLACE_BETWEEN_WHITESPACE, ''),
        profileData?.PhoneNumber?.trim().replace(REGEX.REPLACE_BETWEEN_WHITESPACE, ''),
      )
        .then((phoneCheckResponse: any) => {
          //phoneCheckResponse.VerifiedEmail = 'oa.rahman@apex.com';
          console.log(phoneCheckResponse);
          if (phoneCheckResponse && !phoneCheckResponse.NumberUsed) {
            setPhoneNumberStatus(PhoneNumberStatus.temporarySignUpDone);
            setOptCountdownStartTime(Date.now() + OTP_RESEND_TIMEOUT);
            openNotification('success', 'An OTP was sent to your number');
          } else {
            if (phoneCheckResponse.NumberUsed) {
              setPhoneNumberStatus(PhoneNumberStatus.exists);
            }
          }
        })
        .catch((error) => {
          let axiosError = error as AxiosResponse<{ errors: { '': string[] } }>;
          let errorMessage = axiosError?.data?.errors?.['']?.[0] || 'An error has occurred. Please try again.';
          openNotification('danger', 'Signup', errorMessage);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const onSubmit = useCallback(
    (data: any) => {
      if (phoneNumberStatus === PhoneNumberStatus.unchecked) {
        //call phone number status check api
        triggerPhoneCheck(data);
      }
      setFormData(data);
      if (data.otp) {
        setIsLoading(true);
        signIn('credentials', {
          phoneNumber: data.phoneNumber.trim().replace(REGEX.REPLACE_BETWEEN_WHITESPACE, ''),
          otp: data.otp,
          otpType: VerificationOtpType.PhoneNumberUpdate,
          // callbackUrl: '/',
          redirect: false,
        })
          .then((data) => {
            if (data?.ok === true && data.status === 200) {
              if (onComplete) {
                onComplete();
              }
              queryClient.invalidateQueries([QueryKeys.session]);
              openNotification('success', 'Phone Number Added', 'Number has been added successfully');
            } else if (data?.error !== undefined) {
              let errorMessage = data.error || '';
              openNotification('danger', 'Signup', errorMessage);
            }
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            setIsLoading(false);
            queryClient.invalidateQueries([QueryKeys.profileData]);
          });
      }
    },
    [phoneNumberStatus],
  );
  return (
    <HookForm ref={formRef} onSubmit={onSubmit} config={formConfig} className="space-y-6">
      <div>
        {phoneNumberStatus !== PhoneNumberStatus.temporarySignUpDone && (
          <Input
            name="phoneNumber"
            label="Phone Number *"
            disabled={phoneNumberStatus !== PhoneNumberStatus.unchecked}
            addOnRight={
              phoneNumberStatus !== PhoneNumberStatus.unchecked ? (
                <span className="flex items-center px-4">
                  <IconEdit className="w-5 cursor-pointer" onClick={() => setPhoneNumberStatus(PhoneNumberStatus.unchecked)} />
                </span>
              ) : (
                <></>
              )
            }
          />
        )}
        {phoneNumberStatus == PhoneNumberStatus.exists && <p className="mt-1 text-xl text-red-500">This phone number is already in use.</p>}
      </div>
      {phoneNumberStatus === PhoneNumberStatus.temporarySignUpDone && (
        <div>
          <Input name="otp" label="OTP Code *" inputMode="numeric" autoComplete="one-time-code" specialValue={otpValue} />
          <p className="mb-6 mt-1 text-xl">4 digits code was sent to your phone number.</p>
          <p className="page-subheading mb-4 inline text-xl transition-all sm:text-2xl">
            Didn't receive any OTP?{' '}
            {optCountdownStartTime === null ? (
              <span className="underline-custom cursor-pointer font-medium" onClick={() => triggerPhoneCheck(formData)}>
                Resend
              </span>
            ) : (
              <span className="text-gray-500">
                Resend in <Countdown date={optCountdownStartTime} onComplete={() => setOptCountdownStartTime(null)} renderer={CountDownRenderer} />
              </span>
            )}
          </p>
        </div>
      )}
      <button className={'min-w-[12rem] bg-black text-white max-lg:w-full ' + (isLoading ? 'loading' : '') + ' w-full'}>Proceed</button>
    </HookForm>
  );
};

export default AddOrChangePhoneComponent;
